import { useState } from 'react';
import { supabase } from '../supabase';

function StrengthBar({ password }) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8)  score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const colors = ['#f85149','#f97316','#d29922','#4ade80','#22c55e','#16a34a'];
  const labels = ['Very weak','Weak','Fair','Good','Strong','Very strong'];
  const c = colors[Math.min(score, 5)];
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{ flex:1, height:3, borderRadius:3, background: i<=score ? c : '#2d333b', transition:'background 0.2s' }}/>
        ))}
      </div>
      <div style={{ fontSize:10, color:c, fontWeight:600 }}>{labels[Math.min(score,5)]}</div>
    </div>
  );
}

export default function ResetPassword({ onDone }) {
  const [pass,    setPass]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');
  const [done,    setDone]    = useState(false);

  const s = {
    wrap:  { minHeight:'100vh', background:'#0e1117', display:'flex', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'Outfit',sans-serif" },
    box:   { background:'#161b22', border:'1px solid #21262d', borderRadius:16, padding:36, width:'100%', maxWidth:420 },
    logo:  { textAlign:'center', marginBottom:28 },
    logoT: { fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:900, color:'#fff' },
    title: { fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, color:'#fff', textAlign:'center', marginBottom:6 },
    sub:   { fontSize:13, color:'#8b949e', textAlign:'center', marginBottom:24 },
    label: { fontSize:12, fontWeight:600, color:'#8b949e', display:'block', marginBottom:5 },
    input: { width:'100%', background:'#21262d', border:'1px solid #2d333b', borderRadius:8, padding:'11px 13px', color:'#f5f0e8', fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:"'Outfit',sans-serif" },
    btn:   { width:'100%', padding:13, borderRadius:8, border:'none', background:'linear-gradient(135deg,#16a34a,#4ade80)', color:'#0a1a0a', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:"'Outfit',sans-serif" },
    err:   { background:'rgba(248,81,73,.08)', border:'1px solid rgba(248,81,73,.2)', borderRadius:8, padding:'10px 13px', fontSize:13, color:'#f85149', marginBottom:16 },
    ok:    { background:'rgba(34,197,94,.08)', border:'1px solid rgba(34,197,94,.2)', borderRadius:8, padding:'10px 13px', fontSize:13, color:'#4ade80', marginBottom:16 },
    fi:    { marginBottom:16 },
    link:  { color:'#4ade80', cursor:'pointer', fontWeight:600 },
  };

  const submit = async () => {
    setErr('');
    if (!pass) { setErr('Enter a new password.'); return; }
    if (pass.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(pass)) { setErr('Password must contain at least one uppercase letter.'); return; }
    if (!/[0-9]/.test(pass)) { setErr('Password must contain at least one number.'); return; }
    if (pass !== confirm) { setErr('Passwords do not match.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pass });
    setLoading(false);
    if (error) { setErr(error.message); return; }
    setDone(true);
    window.history.replaceState(null, '', window.location.pathname);
    setTimeout(() => onDone && onDone(), 2200);
  };

  if (done) {
    return (
      <div style={s.wrap}>
        <div style={s.box}>
          <div style={{ textAlign:'center', padding:'10px 0 24px' }}>
            <div style={{ width:56, height:56, background:'rgba(34,197,94,.1)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div style={s.title}>Password updated!</div>
            <div style={{ ...s.sub, marginTop:8 }}>You'll be redirected to sign in shortly.</div>
          </div>
          <button style={s.btn} onClick={() => onDone && onDone()}>Sign In Now</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.wrap}>
      <div style={s.box}>
        <div style={s.logo}>
          <svg width="36" height="36" viewBox="0 0 64 64" fill="none" style={{ display:'block', margin:'0 auto 8px' }}>
            <path d="M10 48 L16 24 L28 36 L32 16 L36 36 L48 24 L54 48 Z" fill="#22c55e"/>
            <rect x="10" y="50" width="44" height="6" rx="3" fill="#22c55e"/>
            <circle cx="32" cy="13" r="4" fill="#86efac"/>
          </svg>
          <div style={s.logoT}>Rent<span style={{ color:'#22c55e' }}>Lord</span></div>
        </div>
        <div style={s.title}>Set new password</div>
        <div style={s.sub}>Choose a strong password for your account.</div>

        {err && <div style={s.err}>{err}</div>}

        <div style={s.fi}>
          <label style={s.label}>New Password</label>
          <input style={s.input} type="password" value={pass} onChange={e => setPass(e.target.value)}
            placeholder="Min 8 chars, 1 uppercase, 1 number" autoFocus onKeyDown={e => e.key === 'Enter' && submit()}/>
          <StrengthBar password={pass}/>
        </div>

        <div style={s.fi}>
          <label style={s.label}>Confirm Password</label>
          <input style={{ ...s.input, borderColor: confirm && pass !== confirm ? '#f85149' : '#2d333b' }}
            type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
            placeholder="Repeat your password" onKeyDown={e => e.key === 'Enter' && submit()}/>
          {confirm && pass !== confirm && (
            <div style={{ fontSize:11, color:'#f85149', marginTop:4 }}>Passwords do not match</div>
          )}
        </div>

        <button style={s.btn} onClick={submit} disabled={loading}>
          {loading ? 'Saving...' : 'Set New Password'}
        </button>
      </div>
    </div>
  );
}
